import { push } from 'react-router-redux';
import unescaper from 'anchor-server-common/utilities/unescaper';
import { PODCAST_EPISODE_AUDIO_TYPES } from '../helpers/serverRenderingUtils';
import { unsetAndRedirectUser } from './user';

export const RECEIVE_CALL_INS = '@@sourceAudio/RECEIVE_CALL_INS';
export const RECEIVE_INTERVIEWS = '@@sourceAudio/RECEIVE_INTERVIEWS';
export const RECEIVE_INTERLUDES = '@@sourceAudio/RECEIVE_INTERLUDES';
export const RECEIVE_MOBILE_RECORDINGS =
  '@@sourceAudio/RECEIVE_MOBILE_RECORDINGS';
export const RECEIVE_AUDIO_LIBRARY = '@@sourceAudio/RECEIVE_AUDIO_LIBRARY';
export const RECEIVE_LOCAL_RECORDING = '@@sourceAudio/RECEIVE_LOCAL_RECORDING';
export const UPDATE_UPLOADED_AUDIO = '@@sourceAudio/UPDATE_UPLOADED_AUDIO';
export const UPDATE_AUDIOS = '@@sourceAudio/UPDATE_AUDIOS';

export const AUDIO_TYPE_LABELS = {};
AUDIO_TYPE_LABELS[PODCAST_EPISODE_AUDIO_TYPES.MOBILE_RECORDING] = 'Record';
AUDIO_TYPE_LABELS[PODCAST_EPISODE_AUDIO_TYPES.UPLOAD] = 'Upload';
AUDIO_TYPE_LABELS[PODCAST_EPISODE_AUDIO_TYPES.INTERVIEW] = 'Interviews';
AUDIO_TYPE_LABELS[PODCAST_EPISODE_AUDIO_TYPES.CALL_IN] = 'Call In';
AUDIO_TYPE_LABELS[PODCAST_EPISODE_AUDIO_TYPES.INTERLUDE] = 'Transitions';
AUDIO_TYPE_LABELS[PODCAST_EPISODE_AUDIO_TYPES.LIBRARY] = 'Library';

const emptyArray = [];
const emptyObject = {};

const initialState = {
  callIns: emptyArray,
  audioLibrary: emptyArray,
  hasFetchedInterludes: false,
  hasFetchedMobileRecordings: false,
  hasFetchedCallIns: false,
  hasFetchedAudioLibrary: false,
  interludes: emptyArray,
  interviews: emptyArray,
  mobileRecordings: emptyArray,
};

// Edit metadata within audio arrays
function audioArrayReducer(state = [], action) {
  const { audios } = action.payload;
  const updatedAudios = state.map(audio => {
    const { audioId } = audio;
    return audios[audioId] ? Object.assign({}, audio, audios[audioId]) : audio;
  });
  return updatedAudios;
}

// callins/interludes/interviews/recordings are reduced to arrays within sourceAudio
// with RECEIVE_<type> actions.
// because the fetch returns all of the current segments from that user, it is safe
// to overwrite the entire state in these scenarios
// however, this may not be the case/may not scale for other fetches
function callInReducer(state = emptyArray, action) {
  switch (action.type) {
    case RECEIVE_CALL_INS: {
      const { audios, userDictionary } = action.payload;
      return audios;
    }
    default:
      return state;
  }
}

function interludeReducer(state = emptyArray, action) {
  switch (action.type) {
    case RECEIVE_INTERLUDES: {
      const { interludes } = action.payload;
      const audioToAdd = interludes.map(interlude => ({
        ...interlude,
        caption: interlude.caption ? unescaper(interlude.caption) : null,
      }));
      return audioToAdd;
    }
    case UPDATE_INTERLUDES: {
    }
    default:
      return state;
  }
}

/**
 * TODO: lots of abstraction can be done here
 */
function recordingReducer(state = emptyArray, action) {
  switch (action.type) {
    case RECEIVE_AUDIO_LIBRARY: {
      const { audios } = action.payload;
      const audioToAdd = audios.map(audio => {
        if (audio.type === PODCAST_EPISODE_AUDIO_TYPES.INTERVIEW) {
          return {
            ...audio,
            caption: identifyInterview(audio),
          };
        }
        // (3.0 migrated endpoint)
        return {
          ...audio,
          caption: audio.caption ? unescaper(audio.caption) : null,
        };
      });
      return audioToAdd;
    }
    case RECEIVE_MOBILE_RECORDINGS: {
      const { mobileRecordings } = action.payload;
      const audioToAdd = mobileRecordings.map(recording => ({
        ...recording,
        caption: recording.caption ? unescaper(recording.caption) : null,
      }));
      return audioToAdd;
    }
    case RECEIVE_LOCAL_RECORDING: {
      const { recording } = action.payload;
      const audioToAdd = {
        audioId: recording.audioId,
        caption: recording.caption,
        color: '#FD6767',
      };
      return [audioToAdd].concat(state);
    }
    case UPDATE_UPLOADED_AUDIO: {
      const { oldAudioId, newAudio } = action.payload;
      const updatedAudios = state.map(audio => {
        if (audio.audioId === oldAudioId) {
          return {
            audioId: newAudio.audioId,
            caption: newAudio.caption || audio.caption,
            color: '#FD6767',
          };
        }
        return Object.assign({}, audio);
      });
      return updatedAudios;
    }
    default:
      return state;
  }
}

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case RECEIVE_CALL_INS: {
      return {
        ...state,
        hasFetchedCallIns: true,
        callIns: callInReducer(state.callIns, action),
      };
    }
    case RECEIVE_AUDIO_LIBRARY: {
      return {
        ...state,
        hasFetchedAudioLibrary: true,
        audioLibrary: recordingReducer(state.audioLibrary, action),
      };
    }
    case RECEIVE_LOCAL_RECORDING: {
      const { recording } = action.payload;
      recording.created = new Date();
      return {
        ...state,
        audioLibrary: recordingReducer(state.audioLibrary, action),
      };
    }
    case RECEIVE_INTERLUDES: {
      return {
        ...state,
        hasFetchedInterludes: true,
        interludes: interludeReducer(state.interludes, action),
      };
    }
    case RECEIVE_LOCAL_RECORDING:
    case RECEIVE_MOBILE_RECORDINGS:
    case UPDATE_UPLOADED_AUDIO: {
      return {
        ...state,
        hasFetchedMobileRecordings: true,
        mobileRecordings: recordingReducer(state.mobileRecordings, action),
      };
    }
    case UPDATE_AUDIOS: {
      // Map thru all user-editable reducers, match any updated audios with new metadata
      // updated audios: { audioId: { audioData }}
      // action.payload.updatedAudios[audioId] = {updatedData}
      return {
        ...state,
        callIns: audioArrayReducer(state.callIns, action),
        audioLibrary: audioArrayReducer(state.audioLibrary, action),
        mobileRecordings: audioArrayReducer(state.mobileRecordings, action),
      };
    }
    default:
      return state;
  }
}

// action creators

function receiveCallIns({ audios, userDictionary }) {
  return {
    type: RECEIVE_CALL_INS,
    payload: {
      audios,
      userDictionary,
    },
  };
}

function receiveAudioLibrary(audios) {
  return {
    type: RECEIVE_AUDIO_LIBRARY,
    payload: {
      audios,
    },
  };
}

function receiveInterludes(interludes) {
  return {
    type: RECEIVE_INTERLUDES,
    payload: {
      interludes,
    },
  };
}

function receiveMobileRecordings(mobileRecordings) {
  return {
    type: RECEIVE_MOBILE_RECORDINGS,
    payload: {
      mobileRecordings,
    },
  };
}

export function receiveLocalRecording(recording) {
  return {
    type: RECEIVE_LOCAL_RECORDING,
    payload: {
      recording: {
        ...recording,
        isUploading: true,
      },
    },
  };
}

export function updateUploadedRecordedAudio(oldAudioId, newAudio) {
  return {
    type: UPDATE_UPLOADED_AUDIO,
    payload: {
      oldAudioId,
      newAudio,
    },
  };
}

export function updateAudios(audios) {
  return {
    type: UPDATE_AUDIOS,
    payload: {
      audios: audios || {},
    },
  };
}

// thunks

export function fetchInterludes() {
  return (dispatch, getState) =>
    fetch('/api/sourceaudio/interlude', {
      credentials: 'same-origin',
    })
      .then(response => {
        if (response.status === 200) {
          return response.json();
        }
        return response;
      })
      .then(responseJson => {
        if (responseJson.status && responseJson.status !== 200) {
          if (responseJson.status === 401) {
            // not logged in
            return dispatch(unsetAndRedirectUser());
          }
          // e.g. no permissions (403)
          return dispatch(push('/404'));
        }
        const { interludes } = responseJson;
        dispatch(receiveInterludes(interludes));
        return interludes;
      });
}

export function fetchMyCallIns() {
  return (dispatch, getState) =>
    fetch('/api/sourceaudio/callin', {
      credentials: 'same-origin',
    })
      .then(response => {
        if (response.status === 200) {
          return response.json();
        }
        return response;
      })
      .then(responseJson => {
        if (responseJson.status && responseJson.status !== 200) {
          if (responseJson.status === 401) {
            // not logged in
            return dispatch(unsetAndRedirectUser());
          }
          // e.g. no permissions (403)
          return dispatch(push('/404'));
        }
        const { audios, userDictionary } = responseJson;
        dispatch(receiveCallIns({ audios, userDictionary }));
        return audios;
      });
}

export function fetchMyLibrary(options = { isCacheBust: false }) {
  return (dispatch, getState) =>
    fetch(
      `/api/sourceaudio/audiolibrary${
        options.isCacheBust ? `?timestamp=${Date.now()}` : ''
      }`,
      {
        credentials: 'same-origin',
      }
    )
      .then(response => {
        if (response.status === 200) {
          return response.json();
        }
        return response;
      })
      .then(responseJson => {
        if (responseJson.status && responseJson.status !== 200) {
          if (responseJson.status === 401) {
            // not logged in
            return dispatch(push('/login'));
          }
          // e.g. no permissions (403)
          return dispatch(push('/404'));
        }
        const { audios } = responseJson;
        dispatch(receiveAudioLibrary(audios));
        return responseJson;
      });
}

export function fetchMyMobileRecordings() {
  return (dispatch, getState) =>
    fetch('/api/sourceaudio/mobilerecording', {
      credentials: 'same-origin',
    })
      .then(response => {
        if (response.status === 200) {
          return response.json();
        }
        return response;
      })
      .then(responseJson => {
        if (responseJson.status && responseJson.status !== 200) {
          if (responseJson.status === 401) {
            // not logged in
            return dispatch(unsetAndRedirectUser());
          }
          // e.g. no permissions (403)
          return dispatch(push('/404'));
        }
        const { mobileRecordings } = responseJson;
        dispatch(receiveMobileRecordings(mobileRecordings));
        return mobileRecordings;
      });
}

export function generateMobileRecordingLink() {
  return (dispatch, getState) =>
    fetch('/api/audio/mobilerecording', {
      method: 'POST',
      credentials: 'same-origin',
    }).then(response => {
      if (response.status !== 200) {
        if (response.status === 401) {
          // not logged in
          return dispatch(unsetAndRedirectUser());
        }
        // e.g. no permissions (403)
        return dispatch(push('/404'));
      }
    });
}

// misc

function identifyInterview(interview) {
  if (interview.contactName) {
    return `Interview with ${unescaper(interview.contactName)}`;
  }
  if (interview.participants) {
    const participants = interview.participants.split(',');
    if (participants.length > 1) {
      return `Interview with ${unescaper(participants.join(', '))}`;
    }
    return `Interview with ${unescaper(participants[0])}`;
  }
  return 'Interview';
}

function identifyCallIn(callIn, userDictionary) {
  const userName = unescaper(userDictionary[callIn.userId].name);
  if (callIn.caption) {
    return `${unescaper(callIn.caption)} - ${userName}`;
  }
  return userName;
}
